import { AbstractControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import * as CompanyEmailValidator from "company-email-validator";

export interface CompanyEmailValidatorOptions {
  /**
   * If true, returns { emailRequired: true } when email is empty.
   * If false, returns null (letting Validators.required handle it).
   * Default: false
   */
  requireEmail?: boolean;
}

/**
 * Validates that an email is a company email (not a personal email like gmail.com).
 * This validator:
 * 1. Trims and lowercases the email
 * 2. Checks if email format is valid
 * 3. Checks if it's a company email using CompanyEmailValidator
 *
 * @param options - Configuration options for the validator
 * @returns ValidatorFn that returns validation errors or null
 *
 * @example
 * ```typescript
 * // Let Validators.required handle empty values
 * new FormControl('', [companyEmailValidator()])
 *
 * // Return emailRequired error for empty values
 * new FormControl('', [companyEmailValidator({ requireEmail: true })])
 * ```
 */
export function companyEmailValidator(options: CompanyEmailValidatorOptions = {}): ValidatorFn {
  const { requireEmail = false } = options;

  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value as string | null | undefined;
    if (!value) {
      return requireEmail ? { emailRequired: true } : null;
    }

    const email = value.trim().toLowerCase();
    if (!email) {
      return requireEmail ? { emailRequired: true } : null;
    }

    // Check if format is valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return { invalidEmail: true };
    }

    const isCompanyEmail = CompanyEmailValidator.isCompanyEmail(email);
    return isCompanyEmail ? null : { companyEmailRequired: true };
  };
}
